import { NameFormat, formatName } from 'src/utils/nameFormatting';

export type GroupMember = { clientId: string; firstName: string; lastName: string };

type Groupable = { id: string; clientId: string; groupId?: string | null };

export type CashflowGroup<T> = {
  /** Stable React key: the shared group id, or the lone item's own id. */
  key: string;
  items: T[];
  members: GroupMember[];
};

/**
 * Collapses the one-row-per-member records of a shared item back into the single item the
 * adviser entered. Rows with no group id stand alone, which is every row predating grouping.
 * Group order follows first appearance, so the table doesn't reshuffle as members load.
 */
export function groupCashflowItems<T extends Groupable>(
  items: T[],
  memberByClientId: Map<string, GroupMember>,
): CashflowGroup<T>[] {
  const groups = new Map<string, CashflowGroup<T>>();

  items.forEach(item => {
    const key = item.groupId ?? item.id;
    const existing = groups.get(key);
    const member = memberByClientId.get(item.clientId);

    if (existing) {
      existing.items.push(item);
      if (member && !existing.members.some(m => m.clientId === member.clientId)) {
        existing.members.push(member);
      }
      return;
    }

    groups.set(key, { key, items: [item], members: member ? [member] : [] });
  });

  return [...groups.values()];
}

/**
 * "Tom H." · "Tom H. & Anna H." · "Tom H. & 2 others" — the column stays one line wide however
 * many members share an item, with the full names left to the cell's tooltip.
 */
export function appliesToLabel(members: GroupMember[]): string {
  const short = members.map(m => formatName(m.firstName, m.lastName, NameFormat.SHORT_SURNAME));
  if (short.length === 0) return '';
  if (short.length === 1) return short[0];
  if (short.length === 2) return `${short[0]} & ${short[1]}`;
  return `${short[0]} & ${short.length - 1} others`;
}

export function appliesToFullNames(members: GroupMember[]): string {
  return members
    .map(m => formatName(m.firstName, m.lastName, NameFormat.NATURAL))
    .filter(Boolean)
    .join(', ');
}
